import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Menu, Shield, User, LogOut, Wallet, Home, Info, ChevronDown, X
} from 'lucide-react';
import NotificationBell from './NotificationBell';

// Blog categories — no icons (clean text-only dropdown)
const BLOG_CATS = [
  { id: 'guide',       label: 'Hướng dẫn'       },
  { id: 'seo',         label: 'Kiến thức Escrow' },
  { id: 'wallet',      label: 'Nạp / Rút tiền'   },
  { id: 'transaction', label: 'Giao dịch'         },
  { id: 'dispute',     label: 'Khiếu nại'         },
  { id: 'crypto',      label: 'Crypto'            },
];

const Navbar = ({ isAuthenticated, user, onLogout }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Desktop blog dropdown: open on hover
  const [blogOpen, setBlogOpen] = useState(false);
  const blogCloseTimer = useRef(null);
  // User avatar dropdown (replaces Radix DropdownMenu)
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Trang chủ',     path: '/',          icon: Home  },
    { name: 'Cách hoạt động', path: '/how-it-works', icon: Info  },
    { name: 'Danh mục',      path: '/categories', icon: Shield },
    { name: 'Liên hệ',       path: '/contact',    icon: User  }
  ];

  const openBlog  = () => { clearTimeout(blogCloseTimer.current); setBlogOpen(true);  };
  const closeBlog = () => { blogCloseTimer.current = setTimeout(() => setBlogOpen(false), 120); };

  // Cleanup pending close-timer khi component unmount (tránh state-update-after-unmount)
  useEffect(() => () => clearTimeout(blogCloseTimer.current), []);

  // Close user menu on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  /* ── Mobile sidebar content (shared between auth states) ──────────── */
  const MobileMenu = () => (
    <div className="flex flex-col space-y-0.5 mt-6">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-2 mb-2">Điều hướng</p>

      {/* Trang chủ + Cách hoạt động */}
      {navLinks.slice(0, 2).map((link) => {
        const Icon = link.icon;
        return (
          <Link key={link.path} to={link.path}
            onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'instant' }); }}
            className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
            <Icon className="h-4 w-4" /> {link.name}
          </Link>
        );
      })}

      {/* Blog — always-expanded, no toggle, indented */}
      <div>
        <Link to="/blog"
          onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'instant' }); }}
          className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
          Blog
        </Link>
        <div className="ml-4 border-l-2 border-blue-100 pl-3 space-y-0.5 mt-0.5 mb-1">
          {BLOG_CATS.map(({ id, label }) => (
            <Link key={id} to={`/blog?category=${id}`}
              onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'instant' }); }}
              className="block px-2 py-1.5 rounded-md text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors">
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* Danh mục + Liên hệ */}
      {navLinks.slice(2).map((link) => {
        const Icon = link.icon;
        return (
          <Link key={link.path} to={link.path}
            onClick={() => { setMobileOpen(false); window.scrollTo({ top: 0, behavior: 'instant' }); }}
            className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
            <Icon className="h-4 w-4" /> {link.name}
          </Link>
        );
      })}

      {/* Auth links in sidebar (mobile only) */}
      {isAuthenticated && (
        <>
          <div className="border-t border-gray-100 pt-2 mt-2 space-y-0.5">
            {['admin', 'super_admin'].includes(user?.role) ? (
              <Link to="/admin" onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                <Shield className="h-4 w-4" /> Admin Panel
              </Link>
            ) : (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  <Shield className="h-4 w-4" /> Dashboard
                </Link>
                <Link to="/profile" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  <User className="h-4 w-4" /> Hồ sơ
                </Link>
                <Link to="/wallet" onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                  <Wallet className="h-4 w-4" /> Ví của tôi
                </Link>
              </>
            )}
            <button onClick={() => { handleLogout(); setMobileOpen(false); }}
              className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors w-full text-left">
              <LogOut className="h-4 w-4" /> Đăng xuất
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
    <nav className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 shadow-sm">
      <div className="max-w-7xl mx-auto px-4">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2">

          {/* ── Left side: hamburger (mobile-left) + Logo ─────────────── */}
          <div className="flex items-center gap-2">
            {/* Hamburger — left side, mobile only */}
            <Button variant="ghost" size="icon"
              aria-label="Mở menu điều hướng"
              onClick={() => setMobileOpen(true)}
              className="md:hidden h-8 w-8 flex-shrink-0">
              <Menu className="h-5 w-5" />
            </Button>

            {/* Logo */}
            <Link to={isAuthenticated ? '/dashboard' : '/'}
              onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
              className="flex items-center space-x-2 transition-opacity hover:opacity-80 flex-shrink-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-sm">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <span className="text-base sm:text-xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                Trunggian<span className="text-blue-400">247</span>
              </span>
            </Link>
          </div>

          {/* ── Desktop Navigation ────────────────────────────────────── */}
          <div className="hidden md:flex items-center space-x-6 flex-1 justify-center">
            {navLinks.slice(0, 2).map((link) => (
              <Link key={link.path} to={link.path}
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="text-sm font-medium text-gray-700 transition-colors hover:text-blue-600">
                {link.name}
              </Link>
            ))}

            {/* Blog — hover dropdown, click = all posts */}
            <div className="relative" onMouseEnter={openBlog} onMouseLeave={closeBlog}>
              <Link to="/blog"
                onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); setBlogOpen(false); }}
                className="flex items-center gap-0.5 text-sm font-medium text-gray-700 hover:text-blue-600 transition-colors">
                Blog <ChevronDown className="h-3.5 w-3.5 mt-px ml-0.5" />
              </Link>

              {blogOpen && (
                <div
                  onMouseEnter={openBlog}
                  onMouseLeave={closeBlog}
                  className="absolute left-0 top-full pt-2 w-44 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 py-1 overflow-hidden">
                    {BLOG_CATS.map(({ id, label }) => (
                      <Link key={id} to={`/blog?category=${id}`}
                        onClick={() => { setBlogOpen(false); window.scrollTo({ top: 0, behavior: 'instant' }); }}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                        {label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.slice(2).map((link) => (
              <Link key={link.path} to={link.path}
                onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
                className="text-sm font-medium text-gray-700 transition-colors hover:text-blue-600">
                {link.name}
              </Link>
            ))}
          </div>

          {/* ── Right side: auth buttons ──────────────────────────────── */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {isAuthenticated ? (
              <>
                {!['admin', 'super_admin'].includes(user?.role) && (
                  <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}
                    className="hidden md:inline-flex transition-all hover:border-blue-600 hover:text-blue-600 text-xs">
                    Dashboard
                  </Button>
                )}
                {['admin', 'super_admin'].includes(user?.role) && (
                  <Button variant="outline" size="sm" onClick={() => navigate('/admin')}
                    className="hidden md:inline-flex transition-all hover:border-blue-600 hover:text-blue-600 text-xs">
                    Admin Panel
                  </Button>
                )}

                <NotificationBell isAuthenticated={isAuthenticated} />

                {/* User avatar dropdown — custom, no Radix */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(v => !v)}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 overflow-hidden"
                    aria-label="Tài khoản"
                  >
                    {user?.avatar
                      ? <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" />
                      : (user?.name || 'U')[0].toUpperCase()}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                      <p className="text-xs text-gray-500 px-3 py-1.5 border-b border-gray-100">{user?.email || 'Tài khoản'}</p>
                      {['admin', 'super_admin'].includes(user?.role) ? (
                        <button onClick={() => { navigate('/admin'); setUserMenuOpen(false); }}
                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                          <Shield className="mr-2 h-4 w-4" /> Admin Panel
                        </button>
                      ) : (
                        <>
                          <button onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }}
                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                            <Shield className="mr-2 h-4 w-4" /> Dashboard
                          </button>
                          <button onClick={() => { navigate('/profile'); setUserMenuOpen(false); }}
                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                            <User className="mr-2 h-4 w-4" /> Hồ sơ
                          </button>
                          <button onClick={() => { navigate('/wallet'); setUserMenuOpen(false); }}
                            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                            <Wallet className="mr-2 h-4 w-4" /> Ví của tôi
                          </button>
                        </>
                      )}
                      <div className="border-t border-gray-100 mt-1" />
                      <button onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                        className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <LogOut className="mr-2 h-4 w-4" /> Đăng xuất
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}
                  className="text-xs sm:text-sm h-8 px-2 sm:px-3 hover:text-blue-600">
                  Đăng nhập
                </Button>
                <Button size="sm" onClick={() => navigate('/register')}
                  className="bg-blue-600 text-white text-xs sm:text-sm h-8 px-2 sm:px-4 hover:bg-blue-700">
                  Đăng ký
                </Button>
              </>
            )}
          </div>

        </div>
      </div>
    </nav>

    {/* ── Mobile drawer overlay — no Radix, pure CSS/state ─────────────── */}
    {mobileOpen && (
      <div className="fixed inset-0 z-[200] md:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        {/* Drawer panel */}
        <div className="absolute left-0 top-0 h-full w-[260px] bg-white shadow-xl overflow-y-auto flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="text-sm font-semibold text-gray-800">Điều hướng</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors"
              aria-label="Đóng menu"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>
          <div className="p-4 flex-1">
            <MobileMenu />
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Navbar;
